import { useMemo, useState } from 'react';
import { ProfileHeader } from '@/components/ProfileHeader';
import { SettingsGroup, SettingsRow } from '@/components/SettingsList';
import { WalletCard } from '@/components/WalletCard';
import { InfoSheet } from '@/components/InfoSheet';
import { OrderRow } from '@/components/OrderRow';
import { useTelegram } from '@/hooks/useTelegram';
import { useOrders } from '@/hooks/useOrders';
import { useBottomSheet } from '@/hooks/useBottomSheet';
import styles from './Profile.module.scss';

type InfoKey = 'payment' | 'gift' | 'support' | 'about';

const INFO_TITLES: Record<InfoKey, string> = {
  payment: 'Payment methods',
  gift: 'Gift history',
  support: 'Support',
  about: 'About Shard',
};

export function ProfilePage() {
  const { user } = useTelegram();
  const { orders } = useOrders();
  const infoSheet = useBottomSheet();
  const [activeInfo, setActiveInfo] = useState<InfoKey | null>(null);

  const hasPremium = useMemo(
    () => orders.some((order) => order.productCategory === 'premium' && order.status === 'completed'),
    [orders],
  );

  const giftOrders = useMemo(() => orders.filter((order) => order.recipient.type === 'gift'), [orders]);

  const openInfo = (key: InfoKey) => {
    setActiveInfo(key);
    infoSheet.open();
  };

  return (
    <div className={styles.page}>
      <ProfileHeader user={user} hasPremium={hasPremium} />

      <WalletCard />

      <SettingsGroup>
        <SettingsRow label="Payment methods" onClick={() => openInfo('payment')} />
        <SettingsRow label="Gift history" onClick={() => openInfo('gift')} />
        <SettingsRow label="Support" onClick={() => openInfo('support')} />
      </SettingsGroup>

      <SettingsGroup>
        <SettingsRow label="About Shard" onClick={() => openInfo('about')} />
      </SettingsGroup>

      <p className={styles.footnote}>Shard v1.0 · Independent marketplace, not affiliated with Telegram</p>

      <InfoSheet bottomSheet={infoSheet} title={activeInfo ? INFO_TITLES[activeInfo] : ''}>
        {activeInfo === 'payment' && (
          <p>
            Purchases are billed against your Shard balance, shown at the top of the app. Connect a TON wallet
            above to pay directly on-chain instead — once connected, it becomes an option at checkout.
          </p>
        )}

        {activeInfo === 'gift' && (
          <div>
            {giftOrders.length === 0 ? (
              <p className={styles.emptyGift}>You haven't sent any gifts yet.</p>
            ) : (
              giftOrders.map((order) => <OrderRow key={order.id} order={order} />)
            )}
          </div>
        )}

        {activeInfo === 'support' && (
          <p>
            Something not working, or a purchase didn't go through? Reach us at{' '}
            <a className={styles.link} href="mailto:support@shard.app">
              support@shard.app
            </a>{' '}
            and we'll help sort it out.
          </p>
        )}

        {activeInfo === 'about' && (
          <>
            <p>Shard is an independent marketplace for Telegram Stars and Premium, built as a Telegram Mini App.</p>
            <p>Version 1.0 · Not affiliated with or endorsed by Telegram.</p>
          </>
        )}
      </InfoSheet>
    </div>
  );
}
